import { cloneElement, type ReactElement } from 'react';
import { Flyout, type FlyoutProps } from 'victory';

import type { LineGraphData } from '~/components/LineGraph/LineGraph';
import type { StreamType } from '~/modules/artist-roster/components/TrackDetails/components/TrackStreamsGraph/TrackStreamsGraph';

const OUTER_CIRCLE_RADIUS = 3;
const INNER_CIRCLE_RADIUS = 2;
const CURSOR_Y1 = '5%';
const CURSOR_Y2 = '85%';

interface LineGraphFlyoutProps extends FlyoutProps {
  streamType?: StreamType;
  dataType?: string;
  data?: LineGraphData[];
  lineColors: string[];
  customFlyoutComponent?: ReactElement | undefined;
  customFlyoutOffset?: number[];
}

const LineGraphFlyout = (props: LineGraphFlyoutProps) => {
  const { lineColors, customFlyoutComponent, customFlyoutOffset, ...rest } =
    props;
  const x = props.x ?? 0;
  const y = props.y ?? 0;
  const xOffset = customFlyoutOffset?.[0] ?? 0;
  const yOffset = customFlyoutOffset?.[1] ?? 0;

  let flyoutComponent;
  if (customFlyoutComponent) {
    // Pass current datapoint to custom flyout component
    const customComponentWithData = cloneElement(customFlyoutComponent, {
      ...rest,
      index: props.index,
      data: props.data,
      streamType: props.streamType,
      lineColors,
    });

    flyoutComponent = (
      <foreignObject
        x={x + xOffset}
        y={y + yOffset}
        width={1}
        height={1}
        overflow="visible"
      >
        {customComponentWithData}
      </foreignObject>
    );
  } else {
    flyoutComponent = <Flyout {...rest} />;
  }
  const lineColor = props.dataType === 'search' ? lineColors[1] : lineColors[0];

  return (
    <g>
      {customFlyoutComponent || props.data?.[0]?.[0].label
        ? flyoutComponent
        : null}
      <circle
        cx={x}
        cy={y}
        r={OUTER_CIRCLE_RADIUS}
        stroke={lineColor}
        fill="none"
      />
      <circle cx={x} cy={y} r={INNER_CIRCLE_RADIUS} fill={lineColor} />
      <line x1={x} x2={x} y1={CURSOR_Y1} y2={CURSOR_Y2} stroke={lineColor} />
    </g>
  );
};

LineGraphFlyout.displayName = 'LineGraphFlyout';
export { LineGraphFlyout };
